/**
 * Quand la réponse est un nombre, le clavier du système gagne.
 *
 * **Signalé par Hedi le 28 août 2026, en testant sur PC** : « le clavier
 * contient uniquement des nombres qui sont désorganisés — pourquoi ne pas
 * utiliser un clavier natif dans ce cas ? ». Les deux moitiés de la remarque
 * sont justes.
 *
 * Désorganisé, parce que `keyboardFor` construit ses rangées à partir des
 * opérateurs de l'exercice : quand il n'y en a aucun, la moitié droite de chaque
 * rangée est vide, les rangées cessent d'avoir la même largeur, et les touches
 * ne s'alignent plus en colonnes.
 *
 * Et inutile, parce qu'un pavé de chiffres dessiné à la main perd contre celui
 * du système dans tous les cas : sur Android c'est celui que l'enfant connaît
 * déjà, sur PC c'est le clavier physique, et les deux savent effacer,
 * sélectionner, coller et annuler sans qu'on le réécrive. MathLive vaut ses
 * 800 ko pour une fraction ou une puissance, pas pour « 96 ».
 *
 * Ce champ n'est donc pas une version dégradée du champ mathématique : c'est le
 * bon outil pour les 413 exercices du pool dont la réponse est un nombre nu.
 * C'est `isPlainNumber` qui tranche, à partir de `tokensFor` — la même source
 * que le clavier, pour que les deux ne puissent pas diverger.
 *
 * Le LaTeX d'un nombre est le nombre lui-même, virgule décimale comprise : la
 * saisie part telle quelle dans `latex`, et le serveur la lit déjà — c'est le
 * repli du 26 août dans `_ce_qui_a_ete_saisi`. Aucun `mathjson`, donc aucun
 * moteur de calcul à attendre.
 */

import { useId } from "react";

import type { MathAnswer } from "@/math/mathjson";
import { Button } from "@/ui/primitives";

/**
 * Le seul caractère que le pavé décimal du système ne garantit pas.
 *
 * `inputmode="decimal"` promet les chiffres et le séparateur, jamais le signe :
 * selon le clavier, « −3 » est intapable. Aucune des 413 réponses nues du pool
 * n'est négative aujourd'hui — mais les nombres relatifs sont au programme de
 * 5ᵉ, donc elles le seront, et un enfant qui ne peut pas *écrire* sa réponse
 * est la pire panne possible de ce champ. Une bascule coûte un bouton.
 */
export function basculerSigne(value: string): string {
  const nu = value.trim();
  if (nu.startsWith("-") || nu.startsWith("\u2212")) return nu.replace(/^[-\u2212]\s*/, "");
  return `-${nu}`;
}

type Props = {
  value: string;
  onChange: (answer: MathAnswer) => void;
  disabled?: boolean;
  label?: string;
};

export function NumberAnswerField({ value, onChange, disabled, label }: Props) {
  const hintId = useId();

  return (
    <div className="number-answer">
      {label ? (
        <label className="number-answer__label" htmlFor={hintId}>
          {label}
        </label>
      ) : null}

      <input
        id={hintId}
        className="number-answer__field"
        type="text"
        /* `decimal` et non `numeric` : le pavé d'Android montre alors la
           virgule, sans laquelle « 5,2 » est intapable — les trois premières
           bonnes réponses de Baya, le 26 août, étaient des décimaux. */
        inputMode="decimal"
        autoComplete="off"
        autoCorrect="off"
        spellCheck={false}
        enterKeyHint="done"
        value={value}
        disabled={disabled}
        onChange={(event) => onChange({ latex: event.target.value, mathjson: null })}
      />

      <Button
        variant="quiet"
        onClick={() => onChange({ latex: basculerSigne(value), mathjson: null })}
        disabled={disabled}
      >
        &minus; / +
      </Button>
    </div>
  );
}
